import { memo } from 'react'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { formatDate, formatTime } from '../../utils/dateFormat'
import {
  formatOrderAmount,
  formatOrderDate,
  getOrderListCreatedDate,
  getPurchaseListStatusLabel,
  getStatusColor,
} from '../../utils/ordersListUtils'
import { getPaymentStatusChipProps } from '../../utils/paymentStatusDisplay'

/** Secondary payment chips shown next to order status (e.g. Accepted). */
const SECONDARY_PAYMENT_CHIP_LABELS = new Set([
  'Awaiting Verification',
  'Partially Paid',
  'Payment Rejected',
  'Edited — awaiting verification',
])

const paginationSx = {
  width: '100%',
  overflowX: 'auto',
  '& .MuiToolbar-root': {
    flexWrap: 'wrap',
    justifyContent: { xs: 'center', sm: 'flex-end' },
    minHeight: { xs: 48, sm: 52 },
    px: { xs: 0.5, sm: 2 },
    gap: 0.5,
  },
}

function OrdersListTable({
  orders,
  loading,
  page,
  rowsPerPage,
  totalCount,
  isSales,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
}) {
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const showInitialLoader = loading && orders.length === 0

  return (
    <Card
      sx={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        position: 'relative',
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {showInitialLoader ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280, p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer
            sx={{
              width: '100%',
              minWidth: 0,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table sx={{ minWidth: isSales ? 880 : 1180 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Date Created</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Shop Id</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Shop Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Sales Order</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }}>Status</TableCell>
                  {!isSales && (
                    <>
                      <TableCell sx={{ fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                        Verification Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                        Verification Time
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a' }} align="right">
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  const statusLabel = getPurchaseListStatusLabel(order, isSales)
                  const paymentChip =
                    !isSales ? getPaymentStatusChipProps(order) : null
                  const showSecondaryPaymentChip =
                    paymentChip &&
                    SECONDARY_PAYMENT_CHIP_LABELS.has(paymentChip.label)
                  return (
                    <TableRow
                      key={order.order_id}
                      onClick={() => onRowClick(order.order_id)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f8f9fb' },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{order.order_id}</TableCell>
                      <TableCell>
                        {formatOrderDate(getOrderListCreatedDate(order))}
                      </TableCell>
                      <TableCell>{order.org_id != null ? order.org_id : '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{order.organization_name || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {Array.isArray(order.sales_order_numbers) &&
                        order.sales_order_numbers.length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25,
                            }}
                          >
                            {order.sales_order_numbers.map((so) => (
                              <Typography
                                key={so}
                                variant="body2"
                                component="span"
                                sx={{ fontFamily: 'ui-monospace, monospace' }}
                              >
                                {so}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            alignItems: 'center',
                          }}
                        >
                          <Chip
                            label={statusLabel}
                            color={getStatusColor(order.order_status, statusLabel)}
                            size="small"
                            variant="outlined"
                          />
                          {showSecondaryPaymentChip ? (
                            <Chip
                              label={paymentChip.label}
                              color={paymentChip.color}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                        </Box>
                      </TableCell>
                      {!isSales && (
                        <>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(order.payment_verified_at, '-')}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatTime(order.payment_verified_at, '-')}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#e90000' }}>
                        {formatOrderAmount(order.amount)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {orders.length === 0 && !loading && (
            <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
              <Typography>No orders found</Typography>
            </Box>
          )}
        </>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
        sx={paginationSx}
      />
    </Card>
  )
}

export default memo(OrdersListTable)
